'use client';
import { formatDistance, isBrowser, readFromLocalStorage } from '@sbt-web/utils';
import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Icon, IconName, IconSize, Button, TextField } from '@sbt-web/ui';
import classnames from 'classnames';
import { getLabelFromGeo, TiresiasClient, CategoryId, TuttaItalia, CategoryStore } from '@sbt-web/networking';
import { useDebounce } from '@sbt-web/hooks';
import { OptimizelySubitoContext } from '@sbt-web/houston-wrapper';
import { Pulse } from '@sbt-web/tracking';
import { useCombobox } from 'downshift';

var itemClasses = {"item":"Item-module_item__EMk6E","selected":"Item-module_selected__p8F5y","icon":"Item-module_icon__bW2jx"};

var ownClasses = {"open":"RecentSearches-module_open__6fJgr","item-icon":"RecentSearches-module_item-icon__n-anw","title":"RecentSearches-module_title__sAjTm","delete-button":"RecentSearches-module_delete-button__jTd3P"};

var classes$1 = {"last-searches-container":"SuggestionResult-module_last-searches-container__bM5TX","location-qualifier":"SuggestionResult-module_location-qualifier__62w2F"};

const SuggestionResult = ({ optionsLabelsQuery, optionsLabelsCategory, geo, }) => {
    return (React.createElement("div", { className: classes$1['last-searches-container'] },
        React.createElement("p", { className: "body-text small" },
            optionsLabelsQuery,
            " ",
            optionsLabelsCategory),
        React.createElement("p", { className: classnames('caption', 'small', classes$1['location-qualifier']) }, geo)));
};

const geogosMapper = (entry, searchNearRegions) => {
    const label = getLabelFromGeo(entry, searchNearRegions);
    return {
        optionLabels: [label],
        inputLabel: label,
        value: {
            entry,
            searchNearRegions: !!searchNearRegions,
            responseFrom: 'geogos',
        },
    };
};
const getOptionLabelQuery = (query) => query ? `${query} in ` : 'Tutti i risultati in ';
const getRecentSearchGeoLabel = (item, searchNearRegions) => {
    if (item.radiusValues == null) {
        const nameToDisplay = geogosMapper(item.geoValues, searchNearRegions).inputLabel;
        return nameToDisplay;
    }
    else {
        const nameToDisplay = item.radiusValues.friendlyName;
        return `Entro ${formatDistance(item.radiusValues?.radiusMeters)} da ${nameToDisplay}`;
    }
};

const RecentSearches = ({ searches, getItemProps, updateOptions, }) => {
    const deleteHistoryItem = (index) => {
        searches.splice(index, 1);
        updateOptions(searches);
    };
    return (React.createElement("div", { className: classnames(ownClasses.open) },
        searches && searches.length > 0 && (React.createElement("div", { className: ownClasses.title },
            React.createElement("p", { className: "body-text semibold normal" }, "Ricerche recenti"))),
        searches?.map((item, index) => (React.createElement("li", { className: classnames(itemClasses.item), key: `${item.query}-${index}`, ...getItemProps({ item, index }) },
            React.createElement(Icon, { name: IconName.Clock, iconSize: IconSize.MD, classes: [itemClasses.icon] }),
            React.createElement(SuggestionResult, { optionsLabelsQuery: getOptionLabelQuery(item?.query), optionsLabelsCategory: item.category.label, geo: getRecentSearchGeoLabel(item.geo, item.searchNearRegions) }),
            React.createElement(Button, { design: "text", onClick: (e) => {
                    e.stopPropagation();
                    deleteHistoryItem(index);
                }, "aria-label": "Cancella", icon: React.createElement(Icon, { name: IconName.Close, iconSize: IconSize.MD }), classes: [ownClasses['delete-button']] }))))));
};
const localStorageKey = 'recent_autocomplete_searches';

const emptyResult = [];
function useTiresias(hadesBasePath, channel, debounceTimeout, useNextVersion, threshold) {
    const { optimizely } = useContext(OptimizelySubitoContext);
    const [items, setItems] = useState([]);
    const [query, setQuery] = useState('');
    const [clientHeader, setClientHeader] = useState();
    // The query can be repeatedly updated but only the debounced
    // value will ever be set, to trigger useEffect.
    const debouncedQuery = useDebounce(query, debounceTimeout);
    /*
     * A/B test: auto in Auto
     */
    useEffect(() => {
        optimizely?.onReady().then(() => {
            const variation = optimizely.activate('auto-in-auto-pro-seller');
            if (variation && variation === 'B') {
                setClientHeader({
                    'X-Subito-Canary-Support': 'auto_in_auto',
                });
            }
        });
    }, [optimizely]);
    const updateQuery = useCallback((q = '') => {
        setQuery(q);
    }, [setQuery]);
    const client = useMemo(() => new TiresiasClient(hadesBasePath, channel, 750), [hadesBasePath, channel]);
    useEffect(() => {
        if (debouncedQuery.length >= 2) {
            client
                .predict(debouncedQuery, threshold, useNextVersion, clientHeader)
                .then((res) => {
                const predictions = res.predictions ?? [];
                const predictionsWithLabels = predictions.map((p) => enrichPrediction(p, !!clientHeader));
                setItems(predictionsWithLabels);
            })
                .catch((e) => {
                console.error(e);
                setItems(emptyResult);
            });
        }
        else {
            setItems(emptyResult);
        }
    }, [client, debouncedQuery, useNextVersion, threshold, clientHeader]);
    return [items, updateQuery];
}
const enrichPrediction = (p, showGoToCategoryAuto) => {
    const [keyword, category, clothingType, regionFeature, cityFeature, townFeature,] = getFeaturesBulk(p, [
        '/query/search_text',
        '/category',
        '/fashion/clothing_type',
        '/geo/region',
        '/geo/city',
        '/geo/town',
    ]);
    const geo = buildGeo(regionFeature, cityFeature, townFeature);
    const label = keyword?.label ?? '';
    return {
        ...p,
        label,
        category: category?.label,
        categoryId: category?.key,
        clothingType: clothingType?.label,
        /*
         * A/B test: auto in Auto
         * If the query string is empty and the category is '2'
         * show only the category label
         */
        goToCategoryAuto: showGoToCategoryAuto && label === '' && category?.key === CategoryId.Auto,
        geo,
    };
};
/**
 * Return an array of filters from a prediction based on the given keys.
 * If a filter is not found, the value will be `undefined`.
 * The returned array will have the same length as the input keys and their order will be preserved.
 * @param prediction
 * @param keys
 * @returns
 */
function getFiltersBulk(prediction, keys) {
    if (!prediction?.filters) {
        return [];
    }
    const featureMap = new Map(prediction.filters.map((f) => [f.uri, f]));
    return keys.map((key) => featureMap.get(key));
}
function getFeaturesBulk(prediction, keys) {
    return getFiltersBulk(prediction, keys).map((f) => f?.values?.[0]);
}
const buildGeo = (regionFeature, cityFeature, townFeature) => {
    const region = mapRegion(regionFeature?.metadata) ?? TuttaItalia;
    const city = mapCity(cityFeature?.metadata);
    const town = mapTown(townFeature?.metadata);
    return { region, city, town };
};
const mapCommonGeoMetadata = (metadata) => ({
    id: metadata.key,
    friendlyName: metadata.friendly_name,
    label: metadata.label,
    value: metadata.value,
    level: metadata.level,
});
const mapRegion = (metadata) => metadata && {
    ...mapCommonGeoMetadata(metadata),
    uri: '/geo/region',
};
const mapCity = (metadata) => metadata && {
    ...mapCommonGeoMetadata(metadata),
    uri: '/geo/city',
    regionId: metadata.region_id,
    istat: metadata.istat,
    shortName: metadata.short_name,
};
const mapTown = (metadata) => metadata && {
    ...mapCommonGeoMetadata(metadata),
    uri: '/geo/town',
    regionId: metadata.region_id,
    cityId: metadata.city_id,
    istat: metadata.istat,
    hasZone: metadata.has_zone,
};
const getFeature = (prediction, uri) => {
    if (!prediction?.filters?.length) {
        return null;
    }
    const filters = prediction.filters.filter((f) => f.uri === uri);
    if (filters.length !== 1) {
        return null;
    }
    return filters[0].values?.[0] || null;
};

const isTiresiasAutocompleteOption = (option) => {
    return (option?.probability !== undefined ||
        option === null);
};

const getRecentSearchLabel = (query, category, geo) => {
    let recentSearchFullLabel = query;
    if (category) {
        recentSearchFullLabel += ` ${category}`;
    }
    if (geo) {
        recentSearchFullLabel += ` [${geo}]`;
    }
    return recentSearchFullLabel;
};
const isOnListing = (location) => location.indexOf('/annunci') > -1;
const homePageData = {
    get '@id'() {
        return 'sdrn:subito:page:homepage';
    },
    get '@type'() {
        return 'Page';
    },
    get pageType() {
        return 'homepage';
    },
};
const listingPageData = {
    get '@id'() {
        return `sdrn:subito:page:${window.location.href}`;
    },
    get '@type'() {
        return 'Page';
    },
    get pageType() {
        return 'listing';
    },
};
const getPageData = () => {
    if (isOnListing(window.location.href)) {
        return listingPageData;
    }
    else {
        return homePageData;
    }
};
class RecommendationMetadata {
    trackingData;
    constructor(service) {
        this.trackingData = {
            '@id': `sdrn:subito:recommendation:${service}-search-bar`,
            '@type': 'RecommendationMetadata',
            listName: service === 'tiresias' ? 'suggestedCategories' : 'lastSearch',
            recommendationType: 'user-based',
        };
    }
    get data() {
        return this.trackingData;
    }
}
class SuggestionView {
    trackingData;
    constructor(items, service) {
        this.trackingData = {
            items,
            service,
        };
    }
    get data() {
        return this.trackingData;
    }
}
const buildViewTrackingObject = (customTracking) => {
    const itemsValue = customTracking.items.map((item, index) => {
        // Build view object tracking for Tiresias
        if (customTracking.service === 'tiresias') {
            const itemCategory = CategoryStore.getCategoryById(item?.filters[1]?.values[0]?.key).friendly;
            return {
                '@id': `sdrn:subito:recommendationitem:tiresias:${itemCategory}:${item?.probability}`,
                rank: index + 1,
                name: item?.filters[0]?.values[0]?.label,
                '@type': 'RecommendationItem',
            };
        }
        // Build View object tracking for Last Search
        else {
            return {
                '@id': 'sdrn:subito:recommendationitem:lastSearch',
                rank: index + 1,
                name: getRecentSearchLabel(item.recentSearchQuery, item.recentSearchCategory, item.geo),
                '@type': 'RecommendationItem',
            };
        }
    });
    return Object.create(null, {
        type: {
            value: 'RecommendationList',
            enumerable: true,
        },
        numItems: {
            value: itemsValue.length,
            enumerable: true,
        },
        items: {
            value: itemsValue,
            enumerable: true,
        },
        '@id': {
            value: customTracking.service === 'tiresias'
                ? 'sdrn:subito:recommendationitem:search'
                : 'sdrn:subito:recommendationitem:lastSearch',
            enumerable: true,
        },
    });
};
class SuggestionViewTracking {
    trackingData;
    constructor(items, service) {
        const customData = new SuggestionView(items, service).data;
        this.trackingData = {
            type: 'View',
            name: service === 'tiresias'
                ? 'Search recommendation list'
                : 'lastSearch recommendation list',
            object: buildViewTrackingObject(customData),
            page: getPageData(),
            recommendation: new RecommendationMetadata(service).data,
        };
    }
    get data() {
        return this.trackingData;
    }
}
class SuggestionSelection {
    trackingData;
    constructor(query, rank, metadata) {
        this.trackingData = {
            query,
            rank,
            metadata,
        };
    }
    get data() {
        return this.trackingData;
    }
}
const buildClickTrackingObject = (customTracking) => {
    return Object.create(null, {
        type: {
            value: 'RecommendationItem',
            enumerable: true,
        },
        '@id': {
            value: `sdrn:subito:recommendationitem:${customTracking.metadata.service === 'tiresias'
                ? // Add conversion of category ID to friendly name
                    `tiresias:${customTracking.metadata.category}:${customTracking.metadata.probability}`
                : 'lastSearch'}`,
            enumerable: true,
        },
        rank: {
            value: customTracking.rank,
            enumerable: true,
        },
        name: {
            value: customTracking.metadata.service === 'tiresias'
                ? customTracking.query
                : getRecentSearchLabel(customTracking.metadata.query, customTracking.metadata.category, customTracking.metadata.geo),
            enumerable: true,
        },
    });
};
class SuggestionClickTracking {
    trackingData;
    constructor(query, rank, metadata) {
        this.trackingData = {
            type: 'Apply',
            name: metadata.service === 'tiresias'
                ? 'Search recommendation selection'
                : 'lastSearch recommendation selection',
            object: buildClickTrackingObject(new SuggestionSelection(query, rank, metadata).data),
            page: getPageData(),
            recommendation: new RecommendationMetadata(metadata.service).data,
        };
    }
    get data() {
        return this.trackingData;
    }
}

var classes = {"container":"index-module_container__T-Gwe","dropdown":"index-module_dropdown__5Pv-J","dropdown--open":"index-module_dropdown--open__2yJmo","item":"index-module_item__n2pN0","selected":"index-module_selected__F9QxQ","back":"index-module_back__9-U4b","fullscreen":"index-module_fullscreen__grP-5","dialogHeader":"index-module_dialogHeader__bWKpE","categoryItem":"index-module_categoryItem__IzDhf","location":"index-module_location__RJ3Al"};

const pulseTracker = isBrowser() ? new Pulse() : null;
const textFieldRef = React.createRef();
const Search = ({ onSelect, initialValue, name = '/query/search_text', updateItems, labelId, options, onRecentSearchSelect, onFocus, assetsBase, placeholder, disableMobileDialog, disableRecentSearches, }) => {
    const [prevInput, setPrevInput] = useState('');
    const [media, setMedia] = useState('');
    useEffect(() => {
        const media = window.matchMedia('(min-width: 768px)');
        setMedia(media.matches ? 'desktop' : 'mobile');
        // Define the listener as a separate function to avoid recreating it on each render
        const updateMedia = () => setMedia(media.matches ? 'desktop' : 'mobile');
        // Use 'change' instead of 'resize' for better performance
        media.addEventListener('change', updateMedia);
        // Cleanup function to remove the event listener
        return () => media.removeEventListener('change', updateMedia);
    }, []);
    const { getComboboxProps, getInputProps, getItemProps, getMenuProps, highlightedIndex, inputValue, reset, selectedItem, selectItem, setInputValue, openMenu, closeMenu, isOpen, } = useCombobox({
        id: 'search-autocomplete',
        items: options?.items,
        labelId,
        initialInputValue: initialValue,
        itemToString: (item) => isTiresiasAutocompleteOption(item)
            ? (getFeature(item, '/query/search_text')?.label ?? '')
            : (item?.query ?? ''),
        onInputValueChange: (changes) => {
            // Every time the items change this index is reset
            // So new searches change it.
            // However selecting an item updates the query, and we don't want to
            // do a new query in that case: the same results should be shown.
            if (changes.inputValue === '') {
                openMenu();
            }
            updateItems(changes.inputValue);
        },
        onSelectedItemChange: (changes) => {
            // Here manage what happens when the field is cleared
            if (highlightedIndex >= 0) {
                closeMenu();
            }
            // Check if we need to nest this logic
            if (changes.selectedItem != null) {
                if (!isTiresiasAutocompleteOption(changes?.selectedItem)) {
                    pulseTracker?.queueEvent(new SuggestionClickTracking(changes.inputValue, highlightedIndex + 1, {
                        service: 'lastSearch',
                        query: getOptionLabelQuery(changes.inputValue),
                        category: changes?.selectedItem.category.label,
                        geo: getRecentSearchGeoLabel(changes?.selectedItem?.geo, changes?.selectedItem?.searchNearRegions),
                    }).data);
                    onRecentSearchSelect?.(changes.selectedItem);
                    textFieldRef.current?.blur();
                }
                else if (isTiresiasAutocompleteOption(changes?.selectedItem)) {
                    const categoryId = changes?.selectedItem?.filters[1]?.values[0]?.key;
                    if (changes?.inputValue != null && categoryId != null) {
                        pulseTracker?.queueEvent(new SuggestionClickTracking(changes?.inputValue, highlightedIndex + 1, {
                            service: 'tiresias',
                            category: categoryId,
                            probability: changes.selectedItem.probability,
                        }).data);
                    }
                    onSelect?.(changes.selectedItem);
                }
                if (media === 'mobile') {
                    textFieldRef.current?.blur();
                }
            }
        },
        onStateChange: ({ type, selectedItem }) => {
            // Only intervene if the selected item hasn't changed.
            // Otherwise let Downshift do the work.
            if (type === useCombobox.stateChangeTypes.ItemClick &&
                selectedItem == null) {
                closeMenu();
                textFieldRef.current?.blur();
            }
        },
    });
    const updateOptions = (opts) => {
        options.items = opts;
        if (opts.length === 0) {
            window.localStorage.removeItem(localStorageKey);
            // close menu when there is no more recent search item
            reset();
            if (media === 'mobile') {
                openMenu();
            }
            else {
                closeMenu();
            }
        }
        else {
            window.localStorage.setItem(localStorageKey, JSON.stringify(opts));
            setInputValue('');
        }
    };
    // Send View tracking to Pulse
    useEffect(() => {
        if (options.items.length === 0) {
            return;
        }
        if (options.kind === 'recent' && isOpen && inputValue === '') {
            const strippedItems = options.items.map((item) => ({
                recentSearchQuery: getOptionLabelQuery(item?.query),
                recentSearchCategory: item.category.label,
                geo: getRecentSearchGeoLabel(item.geo, item.searchNearRegions),
            }));
            pulseTracker?.queueEvent(new SuggestionViewTracking(strippedItems, 'lastSearch').data);
        }
        if (options.kind === 'search' && isOpen && inputValue !== '') {
            const strippedItems = options.items.map((item) => ({
                probability: item.probability,
                filters: item.filters,
            }));
            pulseTracker?.queueEvent(new SuggestionViewTracking(strippedItems, 'tiresias').data);
        }
    }, [inputValue, isOpen, options.kind, options.items]);
    // We search for the input value when opening the dialog so that values from recent searches pass into the Tiresias flow.
    useEffect(() => {
        if (isOpen) {
            updateItems(inputValue);
        }
    }, [inputValue, isOpen, updateItems]);
    useEffect(() => {
        if (initialValue || initialValue === '') {
            setInputValue(initialValue);
        }
    }, [initialValue, setInputValue]);
    const onSubmit = () => {
        if (highlightedIndex >= 0) {
            // The item has been selected from the dropdown
            selectItem(options.items[highlightedIndex]);
        }
        else if (
        // Check what we need here
        isTiresiasAutocompleteOption(selectedItem) &&
            inputValue !== getFeature(selectedItem, '/query/search_text')?.label) {
            // The item has not been selected from the dropdown,
            // we have to create it from the input alone
            const item = {
                probability: 1,
                filters: [
                    {
                        uri: '/query/search_text',
                        label: 'Cosa cerchi?',
                        values: [{ key: inputValue, label: inputValue }],
                    },
                ],
                label: inputValue,
            };
            selectItem(item);
            closeMenu();
        }
    };
    const handleOnFocus = () => {
        setPrevInput(inputValue || '');
        if (media === 'mobile' || options?.items.length > 0) {
            openMenu();
        }
        else {
            closeMenu();
        }
        onFocus?.();
    };
    const cancelSearch = useCallback(() => {
        setInputValue(prevInput);
        closeMenu();
        textFieldRef.current?.blur();
    }, [closeMenu, prevInput, setInputValue]);
    return (
    // the onClick is only there to stop event bubbling
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React.createElement("div", { className: classnames(classes.container, {
            [classes.fullscreen]: isOpen && !disableMobileDialog,
        }), onClick: (e) => {
            e.stopPropagation();
        }, onKeyDown: (e) => {
            e.stopPropagation();
        } },
        React.createElement("div", { ...getComboboxProps(), className: classes.dialogHeader },
            React.createElement("button", { type: "button", className: classes.back, onClick: cancelSearch, "aria-label": "Torna indietro" },
                React.createElement("img", { src: `${assetsBase}/static/icons/cactus/back-arrow.svg`, width: "24", height: "24", alt: "", crossOrigin: "anonymous" })),
            React.createElement(TextField, { size: "large", icon: "Lens", withClearIcon: inputValue?.length > 0, onInputChange: (e) => {
                    setInputValue(e.target.value);
                    openMenu();
                }, onCleared: () => {
                    setPrevInput(inputValue || '');
                    setInputValue('');
                    if (!isOpen) {
                        openMenu();
                    }
                    textFieldRef.current?.focus();
                }, disableNativeAC: true, ...getInputProps({
                    refKey: 'ref',
                    ref: textFieldRef,
                    type: 'search',
                    placeholder: placeholder ?? 'Vespa, iPhone, Bilocale',
                    name,
                    value: inputValue,
                    onFocus: handleOnFocus,
                    onBlur: (e) => {
                        // Check if the blur event has been caused
                        // by clicking the sibling clear button
                        // and if so, suppress it.
                        if (e?.currentTarget?.nextElementSibling != null &&
                            e.currentTarget.nextElementSibling === e.relatedTarget) {
                            // Prevent submitting
                            return;
                        }
                        else if (media === 'desktop') {
                            closeMenu();
                            onSubmit();
                        }
                    },
                    onKeyDown: (event) => {
                        if (event.key === 'Enter') {
                            onSubmit();
                        }
                        if (event.key === 'Escape') {
                            closeMenu();
                        }
                    },
                }) })),
        React.createElement("ul", { ...getMenuProps(), className: classnames(classes.dropdown, {
                [classes['dropdown--open']]: isOpen || disableMobileDialog,
            }) },
            (isOpen || disableMobileDialog) &&
                inputValue?.length >= 2 &&
                options.kind === 'search' &&
                options.items.map((item, index) => (React.createElement("li", { className: classnames(classes.item, classes['size-small'], {
                        [classes.selected]: highlightedIndex === index,
                    }), key: `${item.label}-${index}`, ...getItemProps({ item, index }) },
                    React.createElement(SearchOption, { item: item })))),
            !disableRecentSearches &&
                (isOpen || disableMobileDialog) &&
                inputValue?.length === 0 &&
                options.kind === 'recent' ? (React.createElement(RecentSearches, { getItemProps: getItemProps, highlightedIndex: highlightedIndex, searches: options.items, updateOptions: updateOptions })) : null)));
};
const SearchOption = ({ item, }) => {
    if (item.goToCategoryAuto) {
        return (React.createElement("span", { className: classes.categoryItem },
            React.createElement(Icon, { name: "Bulb", iconSize: IconSize.MD }),
            React.createElement("b", null, item.category)));
    }
    return (React.createElement("div", null,
        React.createElement("p", { className: "body" },
            item.label,
            " ",
            item.category || item.clothingType ? 'in ' : '',
            React.createElement("b", null, item.clothingType ?? item.category)),
        item.geo && (React.createElement("p", { className: `caption small ${classes.location}` },
            React.createElement(GeoLabel, { geo: item.geo })))));
};
const GeoLabel = ({ geo }) => {
    if (geo.town && geo.city) {
        return `${geo.town.value} (${geo.city.shortName})`;
    }
    if (geo.city) {
        return `${geo.city.value} e provincia`;
    }
    return geo.region.value;
};

const SearchAutocomplete = ({ assetsBase, channel = 'web', debounceTimeout = 250, hadesBasePath, initialValue = '', labelId, name = '/query/search_text', onSelect, onDialogOpen, onDialogClose, onRecentSearchSelect, useTiresiasNextVersion, tiresiasThreshold, onFocus, onAutocompleteOptionsChange, placeholder, disableMobileDialog, disableRecentSearches, }) => {
    const [items, updateItems] = useTiresias(hadesBasePath, channel, debounceTimeout, useTiresiasNextVersion, tiresiasThreshold);
    const recentSearches = readFromLocalStorage(localStorageKey, []);
    const options = recentSearches && recentSearches.length > 0 && items.length === 0
        ? {
            kind: 'recent',
            items: recentSearches,
        }
        : {
            kind: items.length > 0 ? 'search' : 'new',
            items: items.length > 0 ? items : [],
        };
    useEffect(() => {
        onAutocompleteOptionsChange?.(items);
    }, [items, onAutocompleteOptionsChange]);
    return (React.createElement(Search, { assetsBase: assetsBase, initialValue: initialValue, labelId: labelId, name: name, onSelect: onSelect, onDialogOpen: onDialogOpen, onDialogClose: onDialogClose, updateItems: updateItems, options: options, onRecentSearchSelect: onRecentSearchSelect, onFocus: onFocus, placeholder: placeholder, disableMobileDialog: disableMobileDialog, disableRecentSearches: disableRecentSearches }));
};

export { SearchAutocomplete, isTiresiasAutocompleteOption };
//# sourceMappingURL=index.js.map
